/**
 * VA medical records list and loop for the Supporting evidence chapter.
 *
 * The section is shown only when "VA or military medical records" is selected
 * on the types of medical records page. The loop itself is optional (see
 * `vaMedicalRecordsOptions.required`): a Veteran can reach the summary with
 * zero facilities. It precedes private medical records because it is spread
 * before them in the chapter's `pages` (see config/form.jsx).
 *
 * arrayBuilderPages requires the order intro, summary, then item pages.
 * Item data shape: see `vaTreatmentFacilityItemSchema` in schema/index.js.
 */
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';

import { isCompletingForm0781 } from '../../../form0781/utils';
import {
  hasVaMedicalRecords,
  navForwardFromSummary,
  onNavForwardIntoLoop,
} from './utils';
import { vaMedicalRecordsOptions } from './vaMedicalRecords/options';
import introPage from './vaMedicalRecords/intro';
import facilityDetailsPage from './vaMedicalRecords/facilityDetails';
import facilityTypePage from './vaMedicalRecords/facilityType';
import facilityConditionsPage from './vaMedicalRecords/facilityConditions';
import facilityDatePage from './vaMedicalRecords/facilityDate';
import facilityEventPage from './vaMedicalRecords/facilityEvent';
import summaryPage from './vaMedicalRecords/summary';

/**
 * Prefix for the VA medical records loop's item pages. Per the Step 4 URL
 * spec in Figma (#157626) each item page is a flat slug with the index last,
 * e.g. supporting-evidence/va-military-facility-type/:index.
 */
const ITEM_PATH_PREFIX = 'supporting-evidence/va-military-facility';

/**
 * First item page path (with :index) — the card Edit target and the
 * add-mode entry point for the loop.
 */
const ITEM_PATH = `${ITEM_PATH_PREFIX}-name/:index`;

export const VA_MEDICAL_RECORDS_ITEM_PATH = ITEM_PATH;

const SUMMARY_PATH = 'supporting-evidence/va-military-summary';

const INTRO_PATH = 'supporting-evidence/va-military-intro';

/**
 * Loop descriptor for `getLoopEntryPath`: Continue from the intro page goes
 * straight to the first item page (add mode) when no facilities exist yet,
 * or the loop summary otherwise.
 */
const VA_MEDICAL_RECORDS_LOOP = {
  arrayPath: vaMedicalRecordsOptions.arrayPath,
  itemPath: ITEM_PATH,
  summaryPath: SUMMARY_PATH,
  // The summary page's arrayBuilderYesNoUI field; the summary's
  // `onNavForward` branches on it the same way platform's default does.
  hasItemsKey: 'view:hasVaTreatmentFacilities',
};

/**
 * The per-facility traumatic event question applies only when the Veteran is
 * completing the online 21-0781 (mental health) form, as determined by
 * `isCompletingForm0781`.
 */
export const showFacilityEventPage = formData =>
  hasVaMedicalRecords(formData) && isCompletingForm0781(formData);

export const vaMedicalRecordsPages = arrayBuilderPages(
  vaMedicalRecordsOptions,
  pageBuilder => ({
    vaMedicalRecordsIntro: pageBuilder.introPage({
      title: 'Request medical records from VA or military healthcare providers',
      path: INTRO_PATH,
      depends: hasVaMedicalRecords,
      // Continue goes straight into the loop: the first item page (add mode)
      // if no facilities exist yet, or the loop summary if they do.
      //
      // By default, the optional-flow intro page (`required: false`) always
      // continues to the summary, even with zero items. We override
      // that so a Veteran's first pass goes straight to item 0 instead of an
      // empty summary. This is a deliberate mix of behaviors of the required
      // and optional multi-page response patterns, not a platform limitation.
      onNavForward: onNavForwardIntoLoop(VA_MEDICAL_RECORDS_LOOP),
      uiSchema: introPage.uiSchema,
      schema: introPage.schema,
    }),
    // Deleting the last facility keeps the Veteran on this summary page,
    // with no cards present, to answer the Yes/No question. The design
    // system's optional-pattern docs describe redirecting to the intro
    // page instead; we intentionally diverge from that (see
    // https://va.ghe.com/software/vets-website/pull/48985).
    vaMedicalRecordsFacilitySummary: pageBuilder.summaryPage({
      title: 'Review your VA or military health facilities',
      path: SUMMARY_PATH,
      depends: hasVaMedicalRecords,
      // Replaces platform's default navForward so the next page is worked
      // out with getPathAfterLoop, which correctly skips this loop's own
      // expanded item pages.
      onNavForward: navForwardFromSummary(VA_MEDICAL_RECORDS_LOOP),
      uiSchema: summaryPage.uiSchema,
      schema: summaryPage.schema,
    }),
    // A card's "Edit" link opens the first active item page, so keep the
    // facility details page first among the item pages.
    vaMedicalRecordsFacility: pageBuilder.itemPage({
      title: 'VA or military health facility details',
      path: ITEM_PATH,
      depends: hasVaMedicalRecords,
      uiSchema: facilityDetailsPage.uiSchema,
      schema: facilityDetailsPage.schema,
    }),
    vaMedicalRecordsFacilityType: pageBuilder.itemPage({
      title: 'Type of VA or military health facility',
      path: `${ITEM_PATH_PREFIX}-type/:index`,
      depends: hasVaMedicalRecords,
      uiSchema: facilityTypePage.uiSchema,
      schema: facilityTypePage.schema,
    }),
    vaMedicalRecordsFacilityConditions: pageBuilder.itemPage({
      title: 'Conditions treated at this facility',
      path: `${ITEM_PATH_PREFIX}-conditions/:index`,
      depends: hasVaMedicalRecords,
      uiSchema: facilityConditionsPage.uiSchema,
      schema: facilityConditionsPage.schema,
    }),
    vaMedicalRecordsFacilityDate: pageBuilder.itemPage({
      title: 'Treatment dates at this facility',
      path: `${ITEM_PATH_PREFIX}-dates/:index`,
      depends: hasVaMedicalRecords,
      uiSchema: facilityDatePage.uiSchema,
      schema: facilityDatePage.schema,
    }),
    vaMedicalRecordsFacilityEvent: pageBuilder.itemPage({
      title: 'Treatment related to traumatic events',
      path: `${ITEM_PATH_PREFIX}-traumatic-events/:index`,
      depends: showFacilityEventPage,
      uiSchema: facilityEventPage.uiSchema,
      schema: facilityEventPage.schema,
    }),
  }),
);

export default vaMedicalRecordsPages;
